import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { Col, Row, Spin } from "antd";

import { CmtPage } from "@app/components/page/CmtPage.tsx";
import { CmtSyncActionCard } from "@app/components/settings/CmtSyncActionCard.tsx";
import { CmtSyncRunsTable } from "@app/components/settings/CmtSyncRunsTable.tsx";
import { syncActionMessages } from "@app/components/settings/syncActionMessages.ts";
import QueryKey from "@app/constants/QueryKeys.ts";
import { SyncAction, SyncRun } from "@app/models/SyncAction.ts";
import { useSyncService } from "@app/services/api/useSyncService.ts";
import { useSyncActions } from "@app/services/hook/useSyncActions.tsx";
import { showNotification } from "@app/services/notification/showNotification.tsx";
import { colors } from "@app/theme.ts";

const sectionTitleStyle = {
  margin: "0 0 1rem",
  color: colors.navy,
  fontSize: "1.25rem",
  fontWeight: 700,
};

export const SettingsPage = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { runAction, stopRun } = useSyncService();
  const { syncActions, isLoading, refetch } = useSyncActions();
  const [startingActions, setStartingActions] = useState<Set<SyncAction>>(
    new Set(),
  );
  const [stoppingActions, setStoppingActions] = useState<Set<SyncAction>>(
    new Set(),
  );

  const toggle =
    (setActions: typeof setStartingActions) =>
    (action: SyncAction, isPending: boolean) => {
      setActions((previous) => {
        const next = new Set(previous);
        if (isPending) {
          next.add(action);
        } else {
          next.delete(action);
        }
        return next;
      });
    };
  const setStarting = toggle(setStartingActions);
  const setStopping = toggle(setStoppingActions);

  const refreshRuns = () => {
    void refetch();
    void queryClient.invalidateQueries({ queryKey: [QueryKey.SYNC_RUNS] });
  };

  const formatAuthor = (run: SyncRun) =>
    run.trigger === "SCHEDULED"
      ? intl.formatMessage({
          id: "settings.trigger.scheduled",
          defaultMessage: "Automatic",
        })
      : run.triggeredByName ?? run.triggeredBy;

  const notifyAlreadyRunning = async (action: SyncAction) => {
    const { data } = await refetch();
    const runningRun = data?.find(
      (status) => status.action === action,
    )?.lastRun;
    showNotification({
      type: "warning",
      title: intl.formatMessage({
        id: "warning.sync.already_running.title",
        defaultMessage: "Already running",
      }),
      description: runningRun
        ? intl.formatMessage(
            {
              id: "warning.sync.already_running.description",
              defaultMessage: "Already running, started by {author} at {time}",
            },
            {
              author: formatAuthor(runningRun),
              time: intl.formatTime(new Date(runningRun.startedAt), {
                hour: "2-digit",
                minute: "2-digit",
              }),
            },
          )
        : intl.formatMessage(syncActionMessages[action].label),
    });
  };

  const onRun = (action: SyncAction) => {
    setStarting(action, true);
    runAction(action)
      .then(() => {
        showNotification({
          type: "info",
          title: intl.formatMessage({
            id: "info.sync.started.title",
            defaultMessage: "Started",
          }),
          description: intl.formatMessage({
            id: "info.sync.started.description",
            defaultMessage: "The result will be displayed on the card.",
          }),
        });
        refreshRuns();
      })
      .catch((error: unknown) => {
        if (isAxiosError(error) && error.response?.status === 409) {
          return notifyAlreadyRunning(action);
        }
        showNotification({
          type: "error",
          title: intl.formatMessage({
            id: "error.title",
            defaultMessage: "Error",
          }),
          description: intl.formatMessage({
            id: "error.sync.description",
            defaultMessage: "Failed to synchronise",
          }),
        });
      })
      .finally(() => setStarting(action, false));
  };

  const onStop = (action: SyncAction, runId: string) => {
    setStopping(action, true);
    stopRun(runId)
      .then(() => {
        showNotification({
          type: "success",
          title: intl.formatMessage({
            id: "info.sync.stopped.title",
            defaultMessage: "Stopped",
          }),
          description: intl.formatMessage({
            id: "info.sync.stopped.description",
            defaultMessage: "The synchronisation can be run again.",
          }),
        });
      })
      .catch((error: unknown) => {
        if (isAxiosError(error) && error.response?.status === 409) {
          showNotification({
            type: "info",
            title: intl.formatMessage({
              id: "info.sync.already_finished.title",
              defaultMessage: "Already finished",
            }),
            description: intl.formatMessage({
              id: "info.sync.already_finished.description",
              defaultMessage: "This run had already finished.",
            }),
          });
          return;
        }
        showNotification({
          type: "error",
          title: intl.formatMessage({
            id: "error.title",
            defaultMessage: "Error",
          }),
          description: intl.formatMessage({
            id: "error.sync.stop.description",
            defaultMessage: "Failed to stop the synchronisation",
          }),
        });
      })
      .finally(() => {
        setStopping(action, false);
        refreshRuns();
      });
  };

  return (
    <CmtPage
      eyebrow={
        <FormattedMessage
          id="settings.eyebrow"
          defaultMessage="Administration"
        />
      }
      title={<FormattedMessage id="app.settings" defaultMessage="Settings" />}
    >
      <h2 style={sectionTitleStyle}>
        <FormattedMessage
          id="settings.title"
          defaultMessage="Synchronisation"
        />
      </h2>
      {isLoading ? (
        <Row justify="center">
          <Spin />
        </Row>
      ) : (
        <Row gutter={[24, 24]}>
          {syncActions.map((syncAction) => (
            <Col key={syncAction.action} xs={24} md={12} xl={8}>
              <CmtSyncActionCard
                syncAction={syncAction}
                isStarting={startingActions.has(syncAction.action)}
                isStopping={stoppingActions.has(syncAction.action)}
                onRun={() => onRun(syncAction.action)}
                onStop={(runId) => onStop(syncAction.action, runId)}
              />
            </Col>
          ))}
        </Row>
      )}
      <h2 style={{ ...sectionTitleStyle, marginTop: "2.5rem" }}>
        <FormattedMessage
          id="settings.history.title"
          defaultMessage="History"
        />
      </h2>
      <CmtSyncRunsTable />
    </CmtPage>
  );
};
